import { useState, useRef, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import type { Project } from '@/types'
import { useVimeoThumbnail } from '@/hooks/useVimeoThumbnail'
import VideoLoader from './VideoLoader'

interface ProjectThumbnailProps {
  project: Project
}

export default function ProjectThumbnail({ project }: ProjectThumbnailProps) {
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const canHoverRef = useRef(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { thumbnailUrl } = useVimeoThumbnail(project.previewVideoId)
  const hasVideo = project.previewVideoId !== ''
  const displayThumbnail = thumbnailUrl || project.thumbnailImage

  useEffect(() => {
    canHoverRef.current = window.matchMedia('(hover: hover)').matches
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (!canHoverRef.current) return
    timeoutRef.current = setTimeout(() => {
      setIsHovering(true)
    }, 200)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsHovering(false)
    setIsVideoPlaying(false)
  }, [])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Listen for Vimeo's postMessage "play" event instead of iframe onLoad
  // so we only reveal the video once actual frames are rendering
  useEffect(() => {
    if (!isHovering) return

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://player.vimeo.com') return

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data

        if (data.event === 'ready' && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({ method: 'addEventListener', value: 'play' }),
            'https://player.vimeo.com'
          )
        }

        if (data.event === 'play') {
          setIsVideoPlaying(true)
        }
      } catch {
        // ignore non-JSON messages
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isHovering])

  return (
    <Link
      to={`/project/${project.slug}`}
      className="group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative aspect-video rounded-card overflow-hidden cursor-pointer bg-black [transform:translateZ(0)] transition-transform duration-300 ease-smooth group-hover:scale-[1.03]">
        {displayThumbnail ? (
          <img
            src={thumbnailUrl || project.thumbnailImage}
            alt={project.name}
            width={1280}
            height={720}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: project.thumbnailGradient }} />
        )}

        {isHovering && hasVideo && (
          <div className="absolute inset-x-0 -top-[20px] -bottom-[20px] overflow-hidden">
            <iframe
              ref={iframeRef}
              src={`https://player.vimeo.com/video/${project.previewVideoId}?autoplay=1&muted=1&loop=1&background=1`}
              className={`absolute inset-x-0 top-0 w-full h-full border-0 transition-opacity duration-500 ${isVideoPlaying ? 'opacity-100' : 'opacity-0'}`}
              allow="autoplay"
              title={`${project.name} preview`}
            />
          </div>
        )}

        {isHovering && hasVideo && !isVideoPlaying && <VideoLoader />}

        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-500" />

        <div className="absolute inset-0 flex flex-col justify-end p-6 bg-gradient-to-t from-black/50 to-transparent">
          <p className="text-caption text-white/50 mb-1">{project.date}</p>
          <h3 className="text-h3 text-white font-medium">{project.name}</h3>
          <p className="hidden md:block text-caption text-white/70">{project.homepageTags}</p>
        </div>
      </div>
    </Link>
  )
}
